// @flow
import * as React from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import { useContext, useState } from 'react';
import {
  Client,
  useDeleteClientNoteMutation, useSetRecordedLineAgreementCheckMutation,
  useSetSoaAgreementCheckMutation,
  useSetTpmoAgreementCheckMutation
} from '../shared';
import { LoadingButton } from '@mui/lab';
import { TextInput } from '@coverright/ui/inputs';
import {
  useCreateClientNoteMutation,
  useUpdateClientNoteMutation
} from '../shared';
import { enqueueSnackbar } from 'notistack';

type Note = {
  note: string,
  id: string,
  isManagerNote?: boolean,
}

type Props = {
  clientId: string,
  initialValue?: Note,
  isManagerNote?: boolean,
  onSubmit: () => void
};

const refetchQueries = ['salesQualifiedFuture', 'salesQualified', 'followUps', 'followUpsFuture', 'contacted']

const NoteForm = (props: Props) => {
  const [note, setNote] = useState(props.initialValue?.note || '');
  const isManagerNote = props.isManagerNote ?? props.initialValue?.isManagerNote ?? false;
  const [create, {loading: createLoading}] = useCreateClientNoteMutation({
    onCompleted: () => enqueueSnackbar(isManagerNote ? 'Manager note created!' : 'Note created!', {variant: 'success'}), refetchQueries
  });
  const [update, {loading: updateLoading}] = useUpdateClientNoteMutation({
    onCompleted: () => enqueueSnackbar('Note saved!', {variant: 'success'}), refetchQueries
  });
  const [deleteNote] = useDeleteClientNoteMutation({
    onCompleted: () => enqueueSnackbar('Note deleted!', {variant: 'info'}), refetchQueries
  });

  const [saveSoaAgreement] = useSetSoaAgreementCheckMutation({
    variables: { clientId: props.clientId }
  });
  const [saveRecordedLineAgreement] = useSetRecordedLineAgreementCheckMutation({
    variables: { clientId: props.clientId }
  });

  const saveSOAIfItWasWrittenInNote = async () => {
    if (!note) return;

    if (note.toLowerCase().includes('soa done')) {
      await saveSoaAgreement();
      await saveRecordedLineAgreement();
    }
  }

  const save = async () => {
    if (props.initialValue?.id) {
      if (note) {
        await update({
          variables: {
            noteId: props.initialValue.id,
            note
          }
        })
        await saveSOAIfItWasWrittenInNote()
      } else {
        await deleteNote({
          variables: {
            noteId: props.initialValue.id,
          }
        })
      }
    } else if (note) {
      await create({
        variables: {
          clientId: props.clientId,
          note,
          isManagerNote: isManagerNote || undefined,
        }
      })
      await saveSOAIfItWasWrittenInNote()
    }
    props.onSubmit();
  }

  const loading = createLoading || updateLoading;


  return (
    <Stack gap={2}>
      <TextInput
        fullWidth
        multiline
        autoFocus
        hideTick
        rows={15}
        label={isManagerNote ? 'Manager note' : 'Agent comments'}
        value={note}
        inputProps={{
          onKeyDown: (e) => {
            if (e.keyCode === 13 && e.ctrlKey) {
              e.preventDefault();
              save();
            }
          },
        }}
        onChange={e => setNote(e.target.value as string)}
      />


      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Button onClick={() => props.onSubmit()} variant={'outlined'}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={save}
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </Stack>

    </Stack>
  );
}

interface ContextProps {
  open: (clientId: string, note?: Note, isManagerNote?: boolean) => void;
}

const NoteFormContext = React.createContext<ContextProps>({
  open: () => {}
})

export const useNoteForm = () => useContext(NoteFormContext)

export function withNoteForm<P>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [note, setNote] = useState<Note>();
    const [clientId, setClientId] = useState<string>();
    const [isManagerNote, setIsManagerNote] = useState(false);

    const open = (id: string, n?: Note, managerNote?: boolean) => {
      setClientId(id);
      setNote(n);
      setIsManagerNote(managerNote ?? n?.isManagerNote ?? false);
      setDialogOpen(true)
    }

    const handleSubmit = () => {
      setDialogOpen(false)
      setTimeout(() => {
        setClientId(undefined);
        setNote(undefined);
        setIsManagerNote(false);
      }, 300);
    }

    const title = isManagerNote
      ? (note ? 'Edit manager note' : 'Add manager note')
      : (note ? 'Edit note' : 'Create note');

    return <NoteFormContext.Provider value={{open}}>
      <Dialog fullWidth maxWidth={'sm'} open={dialogOpen} onClose={handleSubmit}>
        <DialogTitle variant={'h3'}>
          {title}
          {dialogOpen && <ModalCloseButton onClick={handleSubmit} sx={{ position: 'absolute', top: 4, right: 8 }} />}
        </DialogTitle>
        <DialogContent>
          {clientId && <NoteForm initialValue={note} clientId={clientId} isManagerNote={isManagerNote} onSubmit={handleSubmit} />}
        </DialogContent>
      </Dialog>
      <WrappedComponent {...props} />
    </NoteFormContext.Provider>
  }
}
